import React, { useCallback, useEffect, useMemo } from 'react';
import styled from '@tanium/react-emotion-9';
import { useLocation } from 'react-router-dom';
import { css } from '@tanium/style-system';
import { Box } from '@tanium/react-box';
import { Shimmer } from '@tanium/react-shimmer';
import { Well } from '@tanium/react-well';
import { useTranslation } from 'react-i18next';
import { Page, PageHeader } from '@tanium/react-page';
import { Select, ValueType } from '@tanium/react-select';
import { useCurrentRoute } from '../../components/AppContent/useCurrentRoute';
import { useTaniumInstances } from '../../hooks/useTaniumInstances';
import { TaniumInstance } from '../../types';
import { AsyncSuspense } from '../../components/AsyncSuspense';
import { getCMPLink } from '../../utilities/instanceHelper';
import { useTokenReloaderEffect } from '../../auth/useTokenReloaderCallbacks';
import { useAuthContext } from '../../auth';
import { AmpAccessToken } from '../../auth/Auth';

export const IframeContainer = styled(Box)(
  css({
    overflow: 'hidden',
    height: '100%',
  })
);

const Container = styled(Box)(
  css({
    width: '50%',
    alignItems: 'center',
    display: 'flex',
    whiteSpace: 'nowrap',
  })
);

type SelectOption = { label: string; value: TaniumInstance };

export const CMP: React.VFC = () => {
  const { t } = useTranslation();
  const auth = useAuthContext();
  const currentRoute = useCurrentRoute();
  const location = useLocation();
  const [selectedInstance, setSelectedInstance] = React.useState<SelectOption>();
  const [iframeLoaded, setIframeLoaded] = React.useState(false);

  const { linkedTaniumInstances, loadLinkedTaniumInstances } = useTaniumInstances();

  const selectOptions: SelectOption[] = useMemo(() => {
    return linkedTaniumInstances
      ? linkedTaniumInstances
          .filter((instance) => !!instance.cloudProvider)
          .map((instance) => ({ label: instance.displayName, value: instance }))
      : [];
  }, [linkedTaniumInstances]);

  useEffect(() => {
    if (selectedInstance) return;
    if (location.search.startsWith('?id=') && selectedInstance === undefined) {
      const instanceId = location.search.substring(4);
      setSelectedInstance(selectOptions.find((option) => option.value.id === instanceId));
    } else if (selectOptions.length > 0) {
      setSelectedInstance(selectOptions[0]);
    }
  }, [location.search, selectOptions, selectedInstance]);

  const instanceUrl = useMemo(() => {
    if (!selectedInstance) return '';
    if (window.location.origin.startsWith('http://localhost')) {
      return `http://localhost:3000/#/${currentRoute!}`;
    }

    const { displayName, cloudProvider } = selectedInstance.value;
    return getCMPLink(displayName, cloudProvider!, currentRoute);
  }, [currentRoute, selectedInstance]);

  // TODO(TSP-1778): Remove when CMP is explicitly requesting token
  const tokenCallback = useCallback(
    (token: AmpAccessToken) => {
      if (!instanceUrl) {
        return false;
      }
      if (!iframeLoaded) {
        return false;
      }
      const iframe = document.querySelector('iframe');
      const handle = iframe!.contentWindow;
      if (!handle) {
        return false;
      }

      // NOTE, the following is a hack, where the content security policy will block
      // AMP from checking the origin once the iframe content has loaded.
      // postMessage is asynchronous and cannot catch an error, so this gives a
      // quick way to ensure that the iframe is actually loaded.
      try {
        return handle.origin === instanceUrl;
        // eslint-disable-next-line no-empty
      } catch (_) {}

      const message = {
        jwt: token.token,
      };
      let times = 0;
      const intervalId = setInterval(() => {
        handle.postMessage(message, instanceUrl);
        times++;
        // 20 times per second for 10 seconds
        if (times >= 20 * 10) {
          clearInterval(intervalId);
        }
      }, 50);
      return true;
    },
    [instanceUrl, iframeLoaded]
  );

  useTokenReloaderEffect(auth, tokenCallback);

  return (
    <IframeContainer>
      <Page header={<PageHeader heading={''} />}>
        <Well>
          <AsyncSuspense
            state={loadLinkedTaniumInstances}
            loading={
              <Box mt={2} height={1} width="100%">
                <Shimmer />
              </Box>
            }
            failure={t('Failed to load Tanium Instances')}
          >
            <Box width="100%">
              <Container>
                <Box mx={1}>{t('Instance Hostname:')}</Box>
                <Box mx={1}>
                  <Select
                    aria-label={t('Event')}
                    options={selectOptions}
                    isMulti={false}
                    value={selectedInstance}
                    size="sm"
                    styles={{ container: (base) => ({ ...base, width: 200 }) }}
                    onChange={(valueType: ValueType<SelectOption | string>, _action) => {
                      const option = valueType as SelectOption;
                      setSelectedInstance(option);
                    }}
                  />
                </Box>
                {selectedInstance && selectedInstance.value && (
                  <>
                    <Box mx={1}>{t('License ID:')}</Box>
                    <Box mx={1}>{selectedInstance?.value?.licenseId}</Box>
                  </>
                )}
              </Container>
            </Box>
          </AsyncSuspense>
        </Well>
        <iframe
          id="iframe"
          // eslint-disable-next-line @tanium/m6web-i18n/no-text-as-attribute
          title="CMP"
          src={instanceUrl}
          width="100%"
          style={{
            marginTop: '15px',
            border: 'none',
            height: '100%',
          }}
          onLoad={() => {
            setIframeLoaded(true);
          }}
        />
      </Page>
    </IframeContainer>
  );
};
